import { useEffect, useRef, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'
import ReactSignatureCanvas from 'react-signature-canvas'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ClickId, company, CustomerContact } from '../../../utilities/CustomerData'
import html2canvas from 'html2canvas'
import TagManager from 'react-gtm-module'

export function NewChartConnect({ setReload }) {
  const { contactId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const code = queryParams.get('code')
  const isReconnect = queryParams.get('isReconnect')

  const idMeURL = `${process.env.REACT_APP_IDME_API_DOMAIN}/oauth/authorize?client_id=${process.env.REACT_APP_IDME_CLIENT_ID}&redirect_uri=${apiURL}/idme&response_type=code&scope=openid%20http://idmanagement.gov/ns/assurance/ial/2/aal/2&state=${contactId}`
  const [fetchingData, setFetchingData] = useState(true)
  const [contact, setContact] = useState(new CustomerContact())
  const [clickId, setClickId] = useState(new ClickId())

  const [step, setStep] = useState(0)
  const [showKYC, setShowKYC] = useState(true)
  const [agree, setAgree] = useState(false)
  const [signatureText, setSignatureText] = useState('')
  const [submittingForm, setSubmittingForm] = useState(false)
  const [errors, setErrors] = useState({})

  const signatureRef = useRef(null)

  const handleCheckStep3 = async () => {
    const newErrors = {}

    if (!agree)
      newErrors.agree = 'Must agree to conditions above'

    if(!signatureText.trim())
      newErrors.signatureText = 'Signature cannot be empty'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    if(signatureRef.current) {
      const canvas = await html2canvas(signatureRef.current, {
        backgroundColor: null
      })

      const base64String = canvas.toDataURL('image/png').split(',')[1]
      await handleSubmitForm(base64String)
    }
  }

  const handleSubmitForm = async (base64String) => {
    const form = new FormData()
    form.append('uploadChartConsent', JSON.stringify({
      contactId: contactId,
      signatureBase64: base64String.replace('data:image/png;base64,', '')
    }))

    setSubmittingForm(true)

    const uploadedChartConsentResponse = await fetch(`${apiURL}/api/uploadChartConsent?isReconnect=${encodeURIComponent(isReconnect ? 1 : 0)}`, {
      method: 'POST',
      body: form
    })
    const uploadedChartConsentData = await uploadedChartConsentResponse.json()
    setSubmittingForm(false)
    if (uploadedChartConsentData.response.error)
      console.error(uploadedChartConsentData.response.error)
    else {
      setStep(step + 1)

      if(company.companyId === '1d622c43-baf3-45b6-8225-ec055b0e39f6') {
        TagManager.dataLayer({
          dataLayer: {
            event: 'submitted'
          }
        })
      }
      if(clickId.parameterId)
        await fetch(`https://track.protaxpayouts.com/postback?clickid=${clickId.clickid}`)
    }
  }

  useEffect(() => {
      const handleExchangeIDMECode = async () => {
        const form = new FormData()
        form.append('codeInfo', JSON.stringify({
          customerId: contactId,
          code: code
        }))
  
        const exchangeCodeResponse = await fetch(`${apiURL}/api/exchangeCodeIDME`, {
          method: 'POST',
          body: form
        })
        const exchangeCodeData = await exchangeCodeResponse.json()
        if(exchangeCodeData.response.error)
          console.log('RECONNECT')
      }
  
      if(code) {
        console.log(code)
        handleExchangeIDMECode()
        setShowKYC(false)
        setStep(1)
      }
    }, [code])

  useEffect(() => {
    const handleGetLeadContact = async () => {
      const contactResponse = await fetch(`${apiURL}/api/getChartConsentInfo?contactId=${encodeURIComponent(contactId)}`)
      const contactData = await contactResponse.json()
      setFetchingData(false)
      if (contactData.response.error)
        console.error(contactData.response.error)
      else {
        const contact = new CustomerContact(contactData.response.contact)
        if(!code)
          setShowKYC(contactData.response.hasVerification ? false : true)
        setContact(contact)
        setClickId(new ClickId(contactData.response.clickId))
      }
    }

    document.title = `${company.companyName} | Chart Connect`
    handleGetLeadContact()
  }, [])

  return (
    <div className='w-full h-screen flex items-center justify-center'>
      {!fetchingData ? (
        <div className='bg-white p-8 rounded-lg shadow-lg max-w-lg max-sm:w-11/12 max-h-[75%] w-full relative overflow-y-auto'>
          {step === 0 ? (
            <div>
              <p className='font-bold text-3xl text-center text-gray-800 mb-6'>Alright {contact.contactFirstName}, let's connect your IRS documents.</p>
              <div className='flex flex-col space-y-6'>
                <button disabled={!isReconnect && (contact.chartAccessToken)} onClick={() => setStep(step+1)} className={`px-10 py-3 text-white font-semibold rounded-lg shadow-md transform transition-all ${!isReconnect && (contact.chartAccessToken) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                  Submit Tax Authorization Form
                </button>
                {(contact.chartAccessToken && !isReconnect) && (
                  <div className='flex flex-col items-center gap-4'>
                    <p className='text-center'>Your IRS Documents have been requested.</p>
                    <button onClick={() => { setReload(true); navigate('/') }} className='px-10 py-3 text-white font-semibold rounded-lg shadow-md transform bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                      Return to Customer Portal
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : step === 1 ? (
            showKYC ? (
              <div className='w-full flex flex-col items-center justify-center gap-4'>
                <p className='text-lg font-semibold text-center'>Identity Verification</p>
                <p className='text-center'>Before we begin, let's identify who you are.</p>
                <div className='flex justify-center w-full'>
                  <button onClick={() => window.location.href = idMeURL}>
                    <img className='w-56 h-11' src='https://s3.amazonaws.com/idme/developer/idme-buttons/assets/img/verify.svg' />
                  </button>
                </div>
                <p className='italic text-sm text-gray-400'>NOTE: We will be using ALL personal information (including address) for future use. Please make sure your IDme information is completely up to date.</p>
              </div>
            ) : (
              <div className='h-full flex flex-col gap-4'>
              <p className='text-lg font-semibold text-center mb-4'>Tax Information Authorization</p>
              <p className='text-center mb-6'>Please read and sign below.</p>
              <p>As part of this transaction, SETC Pros, Inc. uses Chart Editor Inc. ("Chart") as a third party service provider to request your tax information from the Internal Revenue Service ("IRS") and to disclose it to SETC Pros, Inc.. For this purpose, you need to authorize Chart representatives to request your tax return information from the IRS and provide consent to disclose your tax information to SETC Pros, Inc..</p>
              <p>We want you to feel comfortable with the authorizations and consents that are necessary to effect the retrieval and disclosure of your tax information. If you have any questions regarding the handling of your tax information, please contact us at support@trychart.com.</p>
              <div>
                <p className='font-bold'>Authorization to Request Your Tax Information</p>
                <p className='italic text-gray-600'>Summary: Authorization for Chart to request your tax information from the IRS via forms 4506 and 8821.</p>
              </div>
              <p>You hereby authorize representatives of Chart to request and receive your IRS account transcripts and tax return information (collectively, 'Tax Information') from the IRS for the time period indicated below ('Tax Period') and deliver to Chart the Tax Information and any updates and/or changes to your Tax Information during the Tax Period. You understand that the Tax Information may be received by Chart from the IRS in more than one transaction. This authorization is valid for up to three years or until revoked. You have a right to revoke this authorization in writing at any time (except to the extent information has already been released in reliance upon this authorization) by contacting Chart at support@trychart.com.</p>
              <div>
                <p className='font-bold'>Consent to Disclosure of Tax Return Information</p>
                <p className='italic text-gray-600'>Summary: Authorization for Chart to disclose your tax information to the recipient you indicate.</p>
              </div>
              <p>You hereby authorize Chart to disclose your IRS account transcripts and tax return information that Chart has obtained on your behalf (collectively, “Tax Information”) to SETC Pros, Inc. (“Authorized Recipient”) in connection with a transaction between you and the Authorized Recipient. The Tax Information may include some or all of the following: IRS Transcripts for IRS forms 1040, 1120, 1120S, 1065, 990, 940, 941, Wage & Income, Civil Penalty and 1040 Separate Assessments.</p>
              <p>Unless authorized by law, Chart cannot disclose your Tax Information without your consent. Federal law requires that we provide this notice to you and obtain your consent in order to fulfill your request. If you consent to the disclosure of your Tax Information, Federal law may not protect your Tax Information from further use or distribution. By providing your consent below, Chart will disclose to the Authorized Recipient your Tax Information covered by the period indicated below ("Authorized Disclosure Period"). Your consent to the disclosure of your Tax Information to the Authorized Recipient remains active until revoked by emailing Chart at support@trychart.com. If you believe your tax return information has been disclosed or used improperly in a manner unauthorized by law or without your permission, you may contact the Treasury Inspector General for Tax Administration (TIGTA) by telephone at 1-800-366-4484, or by email at complaints@tigta.treas.gov.</p>
              <p>Please confirm your consent for Chart to electronically sign and file IRS Forms 4506 and 8821 to collect your Tax Information and to disclose your Tax Information to the Authorized Recipient by submitting the form below.</p>
              <p className='font-bold'>Auhtorized Disclosure To:</p>
              <p>SETC Pros, Inc.</p>
              <p className='font-bold'>Signature:</p>
              <p>Name: {contact.contactFirstName + ' ' + contact.contactLastName}</p>
              <p>Date: {new Date().toDateString()}</p>
              <div onClick={() => setAgree(!agree)} className='flex flex-row items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <p className='font-bold text-xs'>By checking this box and signing below, you confirm you have read, understood, and agree to the terms and conditions above.</p>
              </div>
              {errors.agree && <p className='text-red-500 text-xs mt-1'>{errors.agree}</p>}
              <div className="flex flex-col gap-4 w-full p-4">
                <input
                  type="text"
                  placeholder="Sign by typing your full name here"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className="w-full border p-2 rounded-md text-base"
                />
                {errors.signatureText && <p className='text-red-500 text-xs mt-1'>{errors.signatureText}</p>}
                <div className="w-full relative h-32 rounded-md">
                  <div ref={signatureRef} className= "font-signature text-4xl text-center h-32 flex items-start justify-center w-full absolute left-0 top-0 z-10 font-medium italic px-4 py-2">
                    {signatureText}
                  </div>
                </div>
              </div>
              {errors.signature && <p className='text-red-500 text-xs mt-1'>{errors.signature}</p>}
              <div className='flex w-full justify-end'>
                <button
                  disabled={submittingForm}
                  onClick={handleCheckStep3}
                  className={`w-full py-2 mt-4 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 ${submittingForm ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                  I agree
                </button>
              </div>
              {submittingForm && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Submitting Authorization Form...</p>
                </div>
              )}
            </div>
            )
          ) : (
            <div className='flex flex-col items-center gap-4'>
              <p className='font-bold text-2xl'>Congratulations!</p>
              <p>We have successfully requested for your IRS documents. Here's what happens next:</p>
              <p>It will take about 2-3 business days to process your request, but once we have received your documents, we will be ready to calculate your refund!</p>
              <p>In the meantime, click below to access your customer portal to track your application, status of refund, view documents, and pay your fee or login to your IRS account to get an instant calculation.</p>
              <div className='w-full flex flex-col gap-2'>
                <button
                  onClick={() => { setReload(true); navigate('/') }}
                  className='w-full py-2 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                  Go To Customer Portal
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
    </div>
  )
}